import React from "react";
import { Dialog, Box, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ArrowBackIosTwoToneIcon from "@mui/icons-material/ArrowBackIosTwoTone";
import ArrowForwardIosTwoToneIcon from "@mui/icons-material/ArrowForwardIosTwoTone";
import { alpha, useTheme } from "@mui/material/styles";

interface ImageGalleryModalProps {
  images: { image_url: string; type?: string }[];
  open: boolean;
  selectedIndex: number;
  onClose: () => void;
  onNavigate: (direction: "next" | "prev") => void;
}

const ImageGalleryModal: React.FC<ImageGalleryModalProps> = ({
  images,
  open,
  selectedIndex,
  onClose,
  onNavigate,
}) => {
  const theme = useTheme();
  const image = images[selectedIndex];
  const isVideo =
    image?.type === "video" ||
    /\.(mp4|webm|ogg|mov)$/i.test(image?.image_url || "");

  if (!image) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(theme.palette.customColors.blackBorder, 0.4),
            backdropFilter: "blur(8px)",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "60vw",
          height: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            p: 1,
            position: "relative",
          }}
        >
          {isVideo ? (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <video
                src={image.image_url}
                controls
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  borderRadius: 8,
                  background: "#000",
                }}
              />
            </Box>
          ) : (
            <img
              src={image.image_url}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          )}
        </Box>

        <IconButton
          onClick={() => onNavigate("prev")}
          sx={{
            position: "absolute",
            left: 5,
            top: "50%",
            transform: "translateY(-50%)",
            color: theme.palette.customColors?.white,
            backgroundColor: theme.palette.customColors?.blackBorder,
            zIndex: 10,
            "&:hover": {
              backgroundColor: theme.palette.customColors?.blackBorder,
            },
          }}
        >
          <ArrowBackIosTwoToneIcon fontSize="small" />
        </IconButton>

        <IconButton
          onClick={() => onNavigate("next")}
          sx={{
            position: "absolute",
            right: 5,
            top: "50%",
            transform: "translateY(-50%)",
            color: theme.palette.customColors?.white,
            backgroundColor: theme.palette.customColors?.blackBorder,
            zIndex: 10,
            "&:hover": {
              backgroundColor: theme.palette.customColors?.blackBorder,
            },
          }}
        >
          <ArrowForwardIosTwoToneIcon fontSize="small" />
        </IconButton>

        <IconButton
          sx={{
            position: "fixed",
            top: 15,
            right: 25,
            color: theme.palette.customColors?.white,
            backgroundColor: theme.palette.customColors?.blackBorder,
            zIndex: 20,
            width: 36,
            height: 36,
            "&:hover": {
              backgroundColor: theme.palette.customColors?.blackBorder,
              opacity: 0.8,
            },
          }}
          aria-label="close"
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Dialog>
  );
};

export default ImageGalleryModal;
